// WP-07 — office role model (LLD-05 §4 Role enumeration, §11 RBAC matrix).
// D-WP07-1: mirrors the API's Prisma `UserRole` string values rather than
// importing from @steier/shared (which carries no domain types yet); moving
// these into shared is deferred to avoid an all-apps CI/merge surface while
// WP-06 is in flight on the same estate.
export const OFFICE_ROLES = ["OWNER", "OFFICE_MANAGER", "BOOKKEEPER"] as const;

/** Roles that may use THIS app. Foreman is excluded by design (BR-021). */
export type Role = (typeof OFFICE_ROLES)[number];

/** Every role the platform can put in a JWT `role` claim (LLD-01 §12). */
export type PlatformRole = Role | "FOREMAN";

export function isOfficeRole(role: string): role is Role {
  return (OFFICE_ROLES as readonly string[]).includes(role);
}

/**
 * The roles that may CAPTURE a job (O-04 intake) — the single source for both
 * the "+ New job" affordances (canCaptureJobs) and the O-04 `jobs/new` route
 * grant (screens/registry.ts). It is the API's own grant: `POST /jobs` is
 * `@Roles("OWNER", "OFFICE_MANAGER")` (apps/api/src/jobs/jobs.controller.ts:413-414).
 * UX-4a — decision-lane row 72 (ii) re-ruled 2026-10-19 (b): job capture stays
 * OWNER / OFFICE_MANAGER, and the O-04 route is narrowed to the same audience
 * so a bookkeeper can no longer reach a form whose save would 403.
 */
export const JOB_CAPTURE_ROLES: readonly Role[] = ["OWNER", "OFFICE_MANAGER"];

/**
 * Who sees the "+ New job" intake affordance — OWNER / OFFICE_MANAGER, the
 * predicate O-03 has carried since WP-10 (pinned by jobs-screens.test.tsx
 * "bookkeeper sees the list but no + New job affordance"). WP-T-2 (UX v1.6 §3)
 * places the same affordance in the shell, so the predicate lives here once
 * instead of being retyped; the VALUE is unchanged (Track U non-break rule 1 —
 * the UX's "all office roles" wording was lane finding F-T2-2; row 72 (ii),
 * re-ruled 2026-10-19 (b), keeps capture OWNER / OFFICE_MANAGER).
 */
export function canCaptureJobs(role: Role | null | undefined): boolean {
  return role != null && JOB_CAPTURE_ROLES.includes(role);
}
